import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

import { c } from '../../styles/theme';

/**
 * Transient confirmation toast.
 *
 * Fixes from the prototype:
 *   - Was `position: absolute` inside the `.lyx-main` scroll container, so
 *     on a scrolled page it appeared wherever the scrolled content
 *     happened to be rather than pinned to the viewport. Now rendered
 *     through a portal with `position: fixed` (see .lyx-toast).
 *   - Had no aria-live region, so screen reader users never learned that
 *     "Copied", "Saved", or "Deleted" had happened. The live region below
 *     is always mounted — announcements only fire reliably when the region
 *     exists in the DOM *before* its text changes, so the wrapper renders
 *     unconditionally and only the visible bubble is conditional.
 *
 * Visual design is unchanged.
 */
export interface ToastProps {
  /** The message to announce and display. Null/empty hides the bubble. */
  message?: string | null;
}

export default function Toast({ message }: ToastProps) {
  return createPortal(
    <div className="lyx-toast" role="status" aria-live="polite" aria-atomic="true">
      {message ? (
        <div
          style={{
            background: c.surface3,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.3)',
            color: c.text1,
            // Portalled onto document.body, so AppShell's font-family does not
            // reach here either — without this the message renders in the UA serif.
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <Check size={13} color={c.success} aria-hidden="true" />
          {message}
        </div>
      ) : null}
    </div>,
    document.body
  );
}
